import { Routes } from '@angular/router'

import { pendingAiGeneratedData } from '@flowx/designer/modules/ai/common/guards/pending-generated.guard'
import { MediaLibraryNominalApi } from '@flowx/designer/shared/api/media-library-nominal.api'
import { DefaultApplicationPermissionsGuard } from '@flowx/designer/shared/guards/application-permissions.guards'
import {
  ApplicationConfigurationparametersPermissionsResolver,
  ApplicationDependenciesPermissionsResolver,
  ApplicationDocumentTemplatesPermissionsResolver,
  ApplicationEnumerationsPermissionsResolver,
  ApplicationMediaLibraryPermissionsResolver,
  ApplicationNotificationTemplatesPermissionsResolver,
  ApplicationProcessesPermissionsResolver,
  ApplicationSettingsPermissionsResolver,
  ApplicationTaskAllocationRulesPermissionsResolver,
  ApplicationTaskStagesPermissionsResolver,
  ApplicationTaskViewsPermissionsResolver,
} from '@flowx/designer/shared/guards/application-permissions.resolvers'
import { applicationSettingsCanDeactivate } from '@flowx/designer/shared/guards/application-settings.guards'
import { LayoutOptions } from '@flowx/designer/shared/types/layout.types'

import { EnumerationValuesComponent } from '../cms/pages/enumeration-values/enumeration-values.component'
import { SubstitutionTagsComponent } from '../cms/pages/substitution-tags/substitution-tags.component'
import { MEDIA_LIBRARY_API } from '../core/dictionary/media-library.dictionary'
import { dependenciesCanDeactivate } from '../dependencies/guards/dependencies.guard'

export const ApplicationResourcesRoutes: Routes = [
  {
    path: '',
    redirectTo: 'processes',
    pathMatch: 'full',
  },
  {
    path: 'processes',
    canActivateChild: [DefaultApplicationPermissionsGuard('process_read')],
    resolve: { accessRights: ApplicationProcessesPermissionsResolver },
    children: [
      {
        path: '',
        data: { [LayoutOptions.HAS_SIDENAV]: true },
        loadComponent: () =>
          import(
            '@flowx/designer/modules/projects/components/processes-list/processes-list.component'
          ).then(({ FlxProcessesListComponent }) => FlxProcessesListComponent),
      },
      {
        path: ':selectedProcessResourceId',
        loadChildren: () =>
          import('@flowx/designer/modules/process-details/process-details.module').then(
            (m) => m.ProcessDetailsModule
          ),
      },
    ],
  },
  {
    path: 'ui-flows',
    loadChildren: () =>
      import('@flowx/designer/modules/ui-flow/ui-flow-routes').then(
        ({ UiFlowRoutes }) => UiFlowRoutes
      ),
  },
  {
    path: 'reusable-resources',
    loadChildren: () =>
      import('@flowx/designer/modules/reusable-resources/reusable-resources.routes').then(
        ({ ReusableResourcesRoutes }) => ReusableResourcesRoutes
      ),
  },
  {
    path: 'enumerations',
    canActivateChild: [DefaultApplicationPermissionsGuard('enum_read')],
    resolve: { accessRights: ApplicationEnumerationsPermissionsResolver },
    children: [
      {
        path: '',
        data: { [LayoutOptions.HAS_SIDENAV]: true },
        loadComponent: () =>
          import(
            '@flowx/designer/modules/projects/components/enumerations-list/enumerations-list.component'
          ).then(({ FlxEnumerationsListComponent }) => FlxEnumerationsListComponent),
      },
      {
        path: 'values',
        component: EnumerationValuesComponent,
      },
    ],
  },
  {
    path: 'substitution-tags',
    children: [
      {
        path: '',
        data: { [LayoutOptions.HAS_SIDENAV]: true },
        canActivate: [DefaultApplicationPermissionsGuard('enum_read')],
        resolve: { accessRights: ApplicationEnumerationsPermissionsResolver },
        component: SubstitutionTagsComponent,
      },
    ],
  },
  {
    path: 'media-library',
    canActivate: [DefaultApplicationPermissionsGuard('proj_mediafile_read')],
    providers: [{ provide: MEDIA_LIBRARY_API, useClass: MediaLibraryNominalApi }],
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    resolve: { accessRights: ApplicationMediaLibraryPermissionsResolver },
    loadComponent: () =>
      import('@flowx/designer/modules/cms/pages/media-library/media-library.component').then(
        ({ MediaLibraryComponent }) => MediaLibraryComponent
      ),
  },
  {
    path: 'configurationParameters',
    canActivate: [DefaultApplicationPermissionsGuard('config_param_read')],
    resolve: {
      accessRights: ApplicationConfigurationparametersPermissionsResolver,
    },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadComponent: () =>
      import(
        '@flowx/designer/modules/projects/components/configuration-parameters/configuration-parameters.component'
      ).then(({ FlxConfigurationParametersComponent }) => FlxConfigurationParametersComponent),
  },
  {
    path: 'dependencies',
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    canActivate: [DefaultApplicationPermissionsGuard('dependency_read')],
    resolve: { accessRights: ApplicationDependenciesPermissionsResolver },
    canDeactivate: [dependenciesCanDeactivate],
    loadComponent: () =>
      import(
        '@flowx/designer/modules/dependencies/components/dependencies-page/dependencies-page.component'
      ).then(({ DependenciesPageComponent }) => DependenciesPageComponent),
  },
  {
    path: 'settings',
    canActivate: [DefaultApplicationPermissionsGuard('project_read')],
    resolve: { accessRights: ApplicationSettingsPermissionsResolver },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadComponent: () =>
      import('@flowx/designer/modules/settings/application-settings.component').then(
        ({ FlxApplicationSettingsComponent }) => FlxApplicationSettingsComponent
      ),
    canDeactivate: [applicationSettingsCanDeactivate],
  },
  {
    path: 'document-intelligence',
    canActivate: [DefaultApplicationPermissionsGuard('proj_mediafile_read')],
    resolve: { accessRights: ApplicationMediaLibraryPermissionsResolver },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadComponent: () =>
      import(
        '@flowx/designer/modules/ai/modules/documents/components/ai-document-intelligence/ai-document-intelligence.component'
      ).then(({ AiDocumentIntelligenceComponent }) => AiDocumentIntelligenceComponent),
  },
  {
    path: 'views',
    canActivate: [DefaultApplicationPermissionsGuard('task_view_read')],
    resolve: { accessRights: ApplicationTaskViewsPermissionsResolver },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadChildren: () =>
      import('@flowx/designer/modules/views/views.module').then((m) => m.ViewsModule),
  },
  {
    path: 'stages',
    canActivate: [DefaultApplicationPermissionsGuard('stage_read')],
    resolve: { accessRights: ApplicationTaskStagesPermissionsResolver },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadChildren: () =>
      import('@flowx/designer/modules/stages/stages.module').then((m) => m.StagesModule),
  },
  {
    path: 'allocations',
    canActivate: [DefaultApplicationPermissionsGuard('allocation_rule_read')],
    resolve: { accessRights: ApplicationTaskAllocationRulesPermissionsResolver },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadChildren: () =>
      import('@flowx/designer/modules/allocations/allocations.module').then(
        (m) => m.AllocationsModule
      ),
  },
  {
    path: 'notification-templates',
    canActivate: [DefaultApplicationPermissionsGuard('notification_templates_read')],
    resolve: { accessRights: ApplicationNotificationTemplatesPermissionsResolver },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadChildren: () =>
      import('@flowx/designer/modules/notification-templates/notification-templates.module').then(
        (m) => m.NotificationTemplatesModule
      ),
  },
  {
    path: 'document-templates',
    canActivate: [DefaultApplicationPermissionsGuard('document_templates_read')],
    resolve: { accessRights: ApplicationDocumentTemplatesPermissionsResolver },
    data: { [LayoutOptions.HAS_SIDENAV]: true },
    loadChildren: () =>
      import('@flowx/designer/modules/document-templates/documents.module').then(
        (m) => m.DocumentsModule
      ),
  },
  {
    path: 'integration-designer',
    loadChildren: () =>
      import('@flowx/designer/modules/integration-designer/integration-designer-routes').then(
        (m) => m.IntegrationDesignerRoutes
      ),
  },
  {
    path: '',
    loadComponent: () =>
      import(
        '@flowx/designer/modules/merge-conflicts/components/merge-conflicts-outlet/merge-conflicts-outlet.component'
      ).then((m) => m.FlxMergeConflictsOutletComponent),
    loadChildren: () =>
      import('@flowx/designer/modules/merge-conflicts/merge-conflicts-routes').then(
        (m) => m.MergeConflictRoutes
      ),
    outlet: 'mergeConflicts',
  },
  {
    path: 'data-model',
    canDeactivate: [pendingAiGeneratedData()],
    loadComponent: () =>
      import(
        '@flowx/designer/modules/data-model-builder/components/project-data-model/project-data-model.component'
      ).then(({ FlxProjectDataModelComponent }) => FlxProjectDataModelComponent),
  },
]
